<template>
	<WaitDialog :isActive="isWaiting" title="Fetching batch runs..." />

	<v-container fluid>
		<v-navigation-drawer v-model="overlayViewForms" location="right" temporary width="400">
			<v-card flat>
				<v-card-title v-if="batchPolicySummary">
					{{ batchPolicySummary.policyNumber }} Form Codes
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text>
					<v-list v-if="batchPolicySummary">
						<v-list-item v-for="(formCode, index) in batchPolicySummary.formCodes" :key="index">
							<v-list-item-title>{{ formCode }}</v-list-item-title>
						</v-list-item>
					</v-list>
				</v-card-text>
			</v-card>
		</v-navigation-drawer>

		<v-navigation-drawer v-model="overlayPrintConfirmations" location="right" temporary width="440">
			<v-card flat>
				<v-card-title v-if="printConfirmationPolicy">
					{{ printConfirmationPolicy }} Print Confirmations
				</v-card-title>
				<v-divider></v-divider>
				<v-card-text>
					<div v-if="printConfirmationDetails.length === 0 && printMissingPieces.length === 0"
						class="text-medium-emphasis">
						No print confirmations were reported for this policy.
					</div>
					<template v-else>
						<!-- Missing pieces first, highlighted in red -->
						<div v-if="printMissingPieces.length > 0"
							class="text-caption text-error font-weight-bold mb-2">
							Not Mailed ({{ printMissingPieces.length }})
						</div>
						<v-list v-if="printMissingPieces.length > 0" class="pt-0">
							<v-list-item v-for="(miss, index) in printMissingPieces" :key="'miss-' + index"
								class="px-0">
								<v-card variant="outlined" color="error" class="mb-2">
									<v-card-text>
										<div class="d-flex justify-space-between align-center mb-1">
											<span class="text-caption text-medium-emphasis font-weight-bold">Batch</span>
											<span class="font-weight-medium">{{ miss.batchName }}</span>
										</div>
										<div class="d-flex justify-space-between align-center mb-1">
											<span class="text-caption text-medium-emphasis font-weight-bold">Recipient</span>
											<span>{{ miss.party || 'N/A' }}</span>
										</div>
										<div class="d-flex justify-space-between align-center">
											<span class="text-caption text-medium-emphasis font-weight-bold">Status</span>
											<span class="text-error font-weight-medium">Not Mailed</span>
										</div>
									</v-card-text>
								</v-card>
							</v-list-item>
						</v-list>

						<!-- Mailed pieces -->
						<div v-if="printConfirmationDetails.length > 0"
							class="text-caption text-medium-emphasis font-weight-bold mb-2">
							Mailed ({{ printConfirmationDetails.length }})
						</div>
						<v-list v-if="printConfirmationDetails.length > 0" class="pt-0">
							<v-list-item v-for="(confirmation, index) in printConfirmationDetails" :key="index"
								class="px-0">
								<v-card variant="tonal" class="mb-2">
									<v-card-text>
										<div class="d-flex justify-space-between align-center mb-1">
											<span class="text-caption text-medium-emphasis font-weight-bold">Batch</span>
											<span class="font-weight-medium">{{ confirmation.batchName }}</span>
										</div>
										<div class="d-flex justify-space-between align-center mb-1">
											<span class="text-caption text-medium-emphasis font-weight-bold">Vendor</span>
											<span>{{ confirmation.vendor || 'N/A' }}</span>
										</div>
										<div class="d-flex justify-space-between align-center mb-1">
											<span class="text-caption text-medium-emphasis font-weight-bold">Printed</span>
											<span>{{ confirmation.printDateTime || 'N/A' }}</span>
										</div>
										<div class="d-flex justify-space-between align-center mb-1">
											<span class="text-caption text-medium-emphasis font-weight-bold">Sequence #</span>
											<span class="font-mono">{{ confirmation.sequenceNumber || 'N/A' }}</span>
										</div>
										<div class="d-flex justify-space-between align-center">
											<span class="text-caption text-medium-emphasis font-weight-bold">Mailed To</span>
											<span>{{ confirmation.mailedTo || 'N/A' }}</span>
										</div>
									</v-card-text>
								</v-card>
							</v-list-item>
						</v-list>
					</template>
				</v-card-text>
			</v-card>
		</v-navigation-drawer>

		<v-navigation-drawer v-model="overlayAffidavit" location="right" temporary width="520">
			<v-card flat>
				<v-card-title>Affidavit of Mailing</v-card-title>
				<v-card-subtitle>Exela vs MOE piece counts by sort group</v-card-subtitle>
				<v-divider></v-divider>
				<v-card-text>
					<v-data-table :headers="affidavitHeaders" :items="affidavitRows" :items-per-page="-1"
						hide-default-footer density="compact" class="elevation-0" :row-props="affidavitRowProps">
					</v-data-table>
					<v-divider class="my-2"></v-divider>
					<div class="d-flex justify-space-between font-weight-bold px-4 py-1">
						<span>Total</span>
						<span>Exela {{ affidavitTotals.exela }} / MOE {{ affidavitTotals.moe }}</span>
					</div>
				</v-card-text>
			</v-card>
		</v-navigation-drawer>

		<!-- Date Range Selection -->
		<v-row class="mb-2" justify="center" align="center">
			<v-col cols="12" sm="4" md="2">
				<v-text-field v-model="fromDate" label="From Date" type="date" variant="outlined"
					density="comfortable" hide-details />
			</v-col>
			<v-col cols="12" sm="4" md="2">
				<v-text-field v-model="toDate" label="To Date" type="date" variant="outlined"
					density="comfortable" hide-details />
			</v-col>
			<v-col cols="auto">
				<v-btn color="primary" prepend-icon="mdi-magnify" @click="fetchBatchSummaries"
					:disabled="!hasDateFilter">
					Search
				</v-btn>
				<v-btn variant="outlined" prepend-icon="mdi-close" @click="clearDateFilter"
					:disabled="!hasDateFilter" class="ml-2">
					Clear
				</v-btn>
			</v-col>
		</v-row>

		<!-- No Results Message -->
		<v-row v-if="hasSearched && batchSummaries.length === 0" class="mb-6" justify="center">
			<v-col cols="12" md="6">
				<v-alert type="info" variant="tonal">
					No batches found for the selected date range.
				</v-alert>
			</v-col>
		</v-row>

		<!-- Batch Selection -->
		<v-row v-if="batchSummaries.length > 0" class="mb-6" justify="center" align="start">
			<v-col cols="12" md="6">
				<v-select v-model="selectedBatchId" :items="batchOptions" item-title="text" item-value="value"
					label="Select Batch Run" variant="outlined" density="comfortable">
					<template v-slot:item="{ props, item }">
						<v-list-item v-bind="props">
							<template v-slot:append>
								<v-chip :color="batchValidationDisplay(item.raw.validationStatus).color"
									size="small" class="mr-2">
									{{ batchValidationDisplay(item.raw.validationStatus).label }}
								</v-chip>
								<v-chip :color="item.raw.hasErrors ? 'error' : 'success'" size="small">
									{{ item.raw.hasErrors ? 'Failed' : 'Successful' }}
								</v-chip>
							</template>
						</v-list-item>
					</template>
					<template v-slot:selection="{ item }">
						<span>{{ item.title }}</span>
						<v-chip :color="batchValidationDisplay(item.raw.validationStatus).color" size="small"
							class="ml-2">
							{{ batchValidationDisplay(item.raw.validationStatus).label }}
						</v-chip>
						<v-chip :color="item.raw.hasErrors ? 'error' : 'success'" size="small" class="ml-2">
							{{ item.raw.hasErrors ? 'Failed' : 'Successful' }}
						</v-chip>
					</template>
				</v-select>
			</v-col>
			<v-col cols="auto">
				<v-btn
					color="primary"
					prepend-icon="mdi-download"
					@click="downloadBatchSummary"
					:disabled="!selectedBatch"
				>
					Download Summary
				</v-btn>
			</v-col>
		</v-row>		<!-- Dashboard Content (only show when batch is selected) -->
		<template v-if="selectedBatch">
			<!-- Summary Cards -->
			<v-row class="mb-6" justify="center">
				<v-col cols="12" sm="6" md="2">
					<v-card class="card-border-left card-border-blue h-100">
						<v-card-text>
							<div class="text-overline text-medium-emphasis font-weight-bold">Total Processed</div>
							<div class="text-h3 font-weight-bold mt-2 text-blue">{{ selectedBatch.totalPoliciesProcessed }}
							</div>
						</v-card-text>
					</v-card>
				</v-col>

				<v-col cols="12" sm="6" md="2">
					<v-card class="card-border-left card-border-green h-100">
						<v-card-text>
							<div class="text-overline text-medium-emphasis font-weight-bold">Successful</div>
							<div class="text-h3 font-weight-bold mt-2 text-success">{{ successCount }}</div>
							<div class="text-caption text-medium-emphasis mt-2">{{ successRate }}% success rate</div>
						</v-card-text>
					</v-card>
				</v-col>

				<v-col cols="12" sm="6" md="2">
					<v-card class="card-border-left card-border-orange h-100">
						<v-card-text>
							<div class="text-overline text-medium-emphasis font-weight-bold">Excluded</div>
							<div class="text-h3 font-weight-bold mt-2 text-orange">{{ excludedCount }}</div>
							<div class="text-caption text-medium-emphasis mt-2">{{ excludedRate }}% excluded rate</div>
						</v-card-text>
					</v-card>
				</v-col>

				<v-col cols="12" sm="6" md="2">
					<v-card class="card-border-left card-border-red h-100">
						<v-card-text>
							<div class="text-overline text-medium-emphasis font-weight-bold">Failed</div>
							<div class="text-h3 font-weight-bold mt-2 text-error">{{ failedCount }}</div>
							<div class="text-caption text-medium-emphasis mt-2">{{ failureRate }}% failure rate</div>
						</v-card-text>
					</v-card>
				</v-col>

				<v-col cols="12" sm="6" md="2">
					<v-card class="card-border-left card-border-purple h-100">
						<v-card-text>
							<div class="text-overline text-medium-emphasis font-weight-bold">Total Pages</div>
							<div class="text-h3 font-weight-bold mt-2 text-purple">{{ totalPages }}</div>
						</v-card-text>
					</v-card>
				</v-col>

				<v-col cols="12" sm="6" md="2">
					<v-card class="card-border-left h-100" :class="validationStatusBorderClass">
						<v-card-text>
							<div class="text-overline text-medium-emphasis font-weight-bold">Print Validation</div>
							<v-chip class="mt-2" :color="validationStatusColor"
								size="small" label prepend-icon="mdi-circle">
								{{ validationStatusLabel }}
							</v-chip>
							<div class="text-caption text-medium-emphasis mt-2">
								{{ affidavitTotals.exela }} (Exela) of {{ affidavitTotals.moe }} (MoE) pieces&nbsp;printed
							</div>
							<m-btn class="mt-1" style="cursor: pointer;" @click="overlayAffidavit = true">
								Affidavit of Mailing
							</m-btn>
						</v-card-text>
					</v-card>
				</v-col>
			</v-row>

			<!-- Filter and Table -->
			<v-row>
				<v-col cols="12">
					<v-card>
						<!-- Filter Buttons -->
						<v-card-text class="pb-0">
							<div class="d-flex align-center flex-wrap ga-4 mb-4">
								<v-btn-toggle v-model="statusFilter" mandatory density="comfortable">
									<v-btn value="all" color="blue">
										All ({{ selectedBatch.policySummaries?.length || 0 }})
									</v-btn>
									<v-btn value="success" color="success">
										Successful ({{ successCount }})
									</v-btn>
									<v-btn value="excluded" color="warning">
										Excluded ({{ excludedCount }})
									</v-btn>
									<v-btn value="failed" color="error">
										Failed ({{ failedCount }})
									</v-btn>
									<v-btn value="paperless" color="info">
										Paperless ({{ paperlessCount }})
									</v-btn>
									<v-btn value="notpaperless" color="blue-grey">
										Not Paperless ({{ notPaperlessCount }})
									</v-btn>
									<v-btn value="confirmed" color="success">
										Mailed ({{ mailedPieceCount }})
									</v-btn>
									<v-btn value="notconfirmed" color="warning">
										Not Mailed ({{ notMailedPieceCount }})
									</v-btn>
								</v-btn-toggle>
								<v-text-field v-model="tableSearch" label="Search all columns"
									prepend-inner-icon="mdi-magnify" variant="outlined" density="comfortable" clearable
									hide-details style="max-width: 320px;" />
							</div>
						</v-card-text>

						<!-- Data Table -->
						<v-data-table :headers="tableHeaders" :items="displayRows" :search="tableSearch"
							:sort-by="[{ key: 'policyNumber', order: 'asc' }]" item-key="policyNumber" class="elevation-0"
							density="comfortable" hover>
							<!-- eslint-disable vue/valid-v-slot -->
							<template v-slot:item.policyNumber="{ item }">
								<span class="font-mono font-weight-bold">{{ item.policyNumber }}</span>
							</template>

							<!-- eslint-disable vue/valid-v-slot -->
							<template v-slot:item.statusLabel="{ item }">
								<v-chip v-if="item.docProdError" color="error" size="small" text-color="white"
									prepend-icon="mdi-circle">
									Failed
								</v-chip>
								<v-chip v-else-if="item.successful" color="success" size="small" text-color="white"
									prepend-icon="mdi-circle">
									Success
								</v-chip>
								<v-chip v-else-if="item.excludedByTracker || item.excludedByDocProd" color="warning"
									size="small" text-color="white" prepend-icon="mdi-circle">
									Excluded
								</v-chip>
								<v-chip v-else color="error" size="small" text-color="white" prepend-icon="mdi-circle">
									Failed
								</v-chip>
							</template> <!-- eslint-disable vue/valid-v-slot -->
							<template v-slot:item.totalPages="{ item }">
								<span class="font-weight-bold">{{ item.totalPages }}</span>
							</template>

							<!-- eslint-disable vue/valid-v-slot -->
							<template v-slot:item.processingTime="{ item }">
								<span class="text-medium-emphasis">{{ item.processingTime }}</span>
							</template>

							<!-- eslint-disable vue/valid-v-slot -->
							<template v-slot:item.formCodes="{ item }">
								<m-btn v-if="item.formCodes && item.formCodes.length > 0" style="cursor: pointer;"
									@click="selectBatchPolicySummary(item)">
									View ({{ item.formCodes.length }})
								</m-btn>
								<span v-if="!item.formCodes || item.formCodes.length === 0"
									class="text-caption text-medium-emphasis">
									N/A
								</span>
							</template>

							<!-- eslint-disable vue/valid-v-slot -->
							<template v-slot:item.batchName="{ item }">
								<span class="text-medium-emphasis">{{ item.batchName }}</span>
							</template>

							<!-- eslint-disable vue/valid-v-slot -->
							<template v-slot:item.isPaperlessLabel="{ item }">
								<span class="font-weight-medium">{{ item.isPaperlessLabel }}</span>
							</template>

							<!-- eslint-disable vue/valid-v-slot -->
							<template v-slot:item.printStatus="{ item }">
								<span v-if="item.printStatus === 'N/A'" class="text-medium-emphasis">
									N/A
								</span>
								<v-chip v-else-if="item.printStatus === 'Pending'" color="grey"
									size="small" text-color="white" prepend-icon="mdi-clock-outline">
									Pending
								</v-chip>
								<v-chip v-else-if="item.printStatus === 'Mailed'" color="success"
									size="small" text-color="white" prepend-icon="mdi-check-circle"
									style="cursor: pointer;" @click="selectPrintConfirmations(item)">
									Mailed ({{ item.printConfirmations.length }})
								</v-chip>
								<v-chip v-else-if="item.printStatus === 'Partial'" color="warning"
									size="small" text-color="white" prepend-icon="mdi-alert"
									style="cursor: pointer;" @click="selectPrintConfirmations(item)">
									Partial ({{ item.printConfirmations.length }} of {{ item.expectedPieces }})
								</v-chip>
								<v-chip v-else color="error" size="small" text-color="white"
									prepend-icon="mdi-alert-circle"
									style="cursor: pointer;" @click="selectPrintConfirmations(item)">
									Not Mailed ({{ item.missingPieces }})
								</v-chip>
							</template>

							<!-- eslint-disable vue/valid-v-slot -->
							<template v-slot:item.details="{ item }">
								<span v-if="item.docProdError" class="text-error text-caption font-weight-medium">
									{{ getFailureReason(item) }}
								</span>
								<span v-else-if="item.successful" class="text-success text-caption font-weight-medium">
									{{ getArchivalStatus(item) }}
								</span>
								<span v-else-if="item.excludedByTracker || item.excludedByDocProd"
									class="text-orange text-caption font-weight-medium">
									{{ getFailureReason(item) }}
								</span>
								<span v-else class="text-error text-caption font-weight-medium">
									{{ getFailureReason(item) }}
								</span>
							</template> <template v-slot:no-data>
								<div class="text-center pa-8 text-medium-emphasis">
									No policies found for the selected filter.
								</div>
							</template>
						</v-data-table>
					</v-card>
				</v-col>
			</v-row>
		</template>
	</v-container>
</template>

<script lang="ts">
import { ref, onMounted, computed, inject } from 'vue';
import { useCommercialApi } from '@/composables/UseCommercialApi';
import { CommercialApiConfig } from '@/services/CommercialApiConfig';
import { BatchSummary, GetBatchSummariesRequest, BatchPolicySummary, BatchValidationStatus, PrintConfirmation, batchValidationDisplay, isBatchValidationPending } from '@/models/BatchSummaryModels';
import MBtn from '@/components/BaseButton.vue';
import WaitDialog from '@/components/WaitDialog.vue';
import { Tell } from '@/services/Tell';
import { useDisplayFormatters } from '@/composables/UseDisplayFormatters';

// A vendor print confirmation paired with the batch file it was reported in.
// Confirmations live on batchFiles[].policies[] (not the top-level
// policySummaries), and a policy can appear in multiple batch files, so the
// drawer shows the batch alongside each confirmation.
type PrintConfirmationDetail = PrintConfirmation & { batchName: string };

// A piece MoE produced but the vendor did not confirm. `party` is the recipient/interest
// name when known (interest party name, else the policyholder), otherwise empty.
type MissingPiece = { batchName: string; party: string };

export default {
	name: 'BatchDashboard',
	components: {
		MBtn,
		WaitDialog
	},
	route: {
		path: '/batch-dashboard',
		meta: {
			requiresAuth: true,
			title: 'Batch Dashboard'
		}
	},
	setup() {
		const batchSummaries = ref<BatchSummary[]>([]);
		const selectedBatchId = ref<string>('');
		const statusFilter = ref<string>('all');
		const tableSearch = ref<string>('');
		const { getBatchSummaries } = useCommercialApi(CommercialApiConfig);
		const overlayViewForms = ref<boolean>(false);
		const batchPolicySummary = ref<BatchPolicySummary | null>(null);
		const overlayPrintConfirmations = ref<boolean>(false);
		const printConfirmationDetails = ref<PrintConfirmationDetail[]>([]);
		const printMissingPieces = ref<MissingPiece[]>([]);
		const printConfirmationPolicy = ref<string>('');
		const overlayAffidavit = ref<boolean>(false);
		const isWaiting = ref<boolean>(false);
		const tell = inject('tell') as Tell;
		const { formatDateTime } = useDisplayFormatters();

		const fromDate = ref<string>('');
		const toDate = ref<string>('');
		const hasSearched = ref<boolean>(false);

		const hasDateFilter = computed(() => {
			return fromDate.value !== '' || toDate.value !== '';
		});

		const formatBatchDateTime = (date: Date): string => {
			return formatDateTime(date, 'dddd YYYY-MM-DD HH:mm:ss');
		};

		const batchOptions = computed(() => {
			return batchSummaries.value.map(batch => ({
				text: formatBatchDateTime(batch.dateStarted),
				value: batch.id,
				hasErrors: batch.hasErrors,
				validationStatus: batch.validationStatus
			}));
		});

		const selectBatchPolicySummary = (tracker: BatchPolicySummary) => {
			batchPolicySummary.value = tracker;
			overlayViewForms.value = true;
		};

		const selectedBatch = computed(() => {
			return batchSummaries.value.find(batch => batch.id === selectedBatchId.value);
		});

		const successCount = computed(() => {
			if (!selectedBatch.value || !selectedBatch.value.policySummaries) return 0;
			return selectedBatch.value.policySummaries.filter(p => p.successful).length;
		});

		const excludedCount = computed(() => {
			if (!selectedBatch.value || !selectedBatch.value.policySummaries) return 0;
			return selectedBatch.value.policySummaries.filter(p => !p.docProdError && (p.excludedByTracker || p.excludedByDocProd)).length;
		}); const failedCount = computed(() => {
			if (!selectedBatch.value || !selectedBatch.value.policySummaries) return 0;
			return selectedBatch.value.policySummaries.filter(p => p.docProdError || (!p.successful && !p.excludedByTracker && !p.excludedByDocProd)).length;
		});

		const paperlessCount = computed(() => {
			if (!selectedBatch.value || !selectedBatch.value.policySummaries) return 0;
			return selectedBatch.value.policySummaries.filter(p => p.isPaperless).length;
		});

		const notPaperlessCount = computed(() => {
			if (!selectedBatch.value || !selectedBatch.value.policySummaries) return 0;
			return selectedBatch.value.policySummaries.filter(p => !p.isPaperless).length;
		});

		const totalPages = computed(() => {
			if (!selectedBatch.value || !selectedBatch.value.policySummaries) return 0;
			return selectedBatch.value.policySummaries.reduce((sum, p) => sum + p.totalPages, 0);
		});

		const validationStatus = computed<BatchValidationStatus | null>(() => {
			return selectedBatch.value ? selectedBatch.value.validationStatus : null;
		});

		const statusDisplay = computed(() => batchValidationDisplay(validationStatus.value));
		// True until validation runs. While pending, per-policy print status is "Pending" (unknown)
		// rather than "Not Mailed", and the Mailed/Not Mailed tab counts read 0 — we haven't checked
		// mailing yet. Discrepancy/Validated are terminal (validation ran), so they are not pending.
		const isValidationPending = computed(() => isBatchValidationPending(validationStatus.value));
		const validationStatusLabel = computed(() => statusDisplay.value.label);
		const validationStatusColor = computed(() => statusDisplay.value.color);
		const validationStatusBorderClass = computed(() =>
			statusDisplay.value.color === 'success' ? 'card-border-green'
				: statusDisplay.value.color === 'warning' ? 'card-border-orange'
					: 'card-border-grey');

		// Affidavit of Mailing: the fixed sort-group taxonomy Exela reports against.
		// Groups absent from the batch show 0. BATCH3AGT/BATCH10 are Exela-only groups
		// MOE never produces, so they always read 0 on the MOE side.
		const AFFIDAVIT_SORT_GROUPS = [
			'AGENT', 'BATCH3', 'BATCH3INT', 'BATCH3AGT', 'BATCH3OR', 'BATCH10', 'CERTMAIL',
			'FOR0006PRT', 'FOR0012PRT', 'FOR0050PRT', 'FOR0150PRT', 'FOR0450PRT', 'FOR2000PRT', 'FOR9999PRT',
			'INS0006PRT', 'INS0012PRT', 'INS0050PRT', 'INS0150PRT', 'INS0450PRT', 'INS2000PRT', 'INS9999PRT',
			'INTEREST'
		];

		const affidavitHeaders = [
			{ title: 'Sort Group', key: 'sortGroup', sortable: false, align: 'start' as const },
			{ title: 'Exela Pieces', key: 'exela', sortable: false, align: 'end' as const },
			{ title: 'MOE Pieces', key: 'moe', sortable: false, align: 'end' as const }
		];

		// Interest batches count one piece per interest party; every other batch counts
		// one piece per policy (mirrors the docgen PrintReportGenerator MOE-piece logic).
		const isInterestGroup = (name: string): boolean => name === 'INTEREST' || name === 'BATCH3INT';

		// One row per sort group: Exela = count of vendor print confirmations in that
		// batch; MOE = pieces we produced for that batch.
		const affidavitRows = computed(() => {
			const batch = selectedBatch.value;
			return AFFIDAVIT_SORT_GROUPS.map(sortGroup => {
				let exela = 0;
				let moe = 0;
				if (batch && batch.batchFiles) {
					for (const bf of batch.batchFiles) {
						if (bf.batchName !== sortGroup || !bf.policies) continue;
						for (const p of bf.policies) {
							exela += p.printConfirmation ? p.printConfirmation.length : 0;
							moe += isInterestGroup(sortGroup)
								? (p.interestEntries ? p.interestEntries.length : 0)
								: 1;
						}
					}
				}
				return { sortGroup, exela, moe };
			});
		});

		const affidavitTotals = computed(() => {
			return affidavitRows.value.reduce((acc, r) => {
				acc.exela += r.exela;
				acc.moe += r.moe;
				return acc;
			}, { exela: 0, moe: 0 });
		});

		// Tint each row: green when Exela and MOE match, red when they differ. Empty
		// groups (0/0) are left neutral so the table isn't a wall of green.
		const affidavitRowProps = (data: { item: { sortGroup: string; exela: number; moe: number } }) => {
			const { exela, moe } = data.item;
			if (exela === 0 && moe === 0) return {};
			return { class: exela === moe ? 'aom-row-match' : 'aom-row-mismatch' };
		};

		// Combine vendor print confirmations across every batch file for a policy.
		// printConfirmation is stamped onto batchFiles[].policies[], and a policy can
		// appear in more than one batch file, so we match on
		// (policyNumber, transactionType) and flatten — tagging each confirmation with
		// the batch it came from for the detail drawer. amendmentNumber is intentionally
		// excluded to mirror the docgen reconciler: the print vendor (Exela) does not
		// echo it back, and (policy, transactionType) is unique within a run.
		const getPrintConfirmations = (policy: BatchPolicySummary): PrintConfirmationDetail[] => {
			if (!selectedBatch.value || !selectedBatch.value.batchFiles) return [];

			const details: PrintConfirmationDetail[] = [];
			for (const batchFile of selectedBatch.value.batchFiles) {
				if (!batchFile.policies) continue;
				const matches = batchFile.policies.filter(p =>
					p.policyNumber === policy.policyNumber &&
					p.transactionType === policy.transactionType);
				for (const match of matches) {
					if (!match.printConfirmation) continue;
					for (const confirmation of match.printConfirmation) {
						details.push({ ...confirmation, batchName: batchFile.batchName });
					}
				}
			}
			return details;
		};

		// A policy is "fully mailed" when the vendor confirmed every piece MoE produced for it
		// (across all its print batches). Fewer confirmations than expected = partial; zero =
		// not mailed. A policy with no print batch (expected 0) is neither — it renders N/A.
		const isFullyMailed = (policy: BatchPolicySummary): boolean => {
			const expected = getExpectedPieces(policy);
			return expected > 0 && getPrintConfirmations(policy).length >= expected;
		};

		// MoE pieces expected for a policy — the denominator for "how many should Exela have
		// mailed". Mirrors getPrintConfirmations' (policyNumber, transactionType) matching and
		// the affidavit MoE-piece rule: one piece per interest party for interest batches,
		// otherwise one piece per batch entry.
		const getExpectedPieces = (policy: BatchPolicySummary): number => {
			if (!selectedBatch.value || !selectedBatch.value.batchFiles) return 0;

			let count = 0;
			for (const bf of selectedBatch.value.batchFiles) {
				if (!bf.policies) continue;
				for (const p of bf.policies) {
					if (p.policyNumber !== policy.policyNumber || p.transactionType !== policy.transactionType) continue;
					count += isInterestGroup(bf.batchName)
						? (p.interestEntries ? p.interestEntries.length : 0)
						: 1;
				}
			}
			return count;
		};

		// Pieces MoE produced that the vendor did not confirm, for the detail drawer. Mirrors
		// the expected/confirmed matching used elsewhere:
		// - non-interest batch: one expected piece; missing when it has no confirmation
		//   (party = policyholder name).
		// - interest batch: one piece per interest party; missing = expected − confirmed. Missing
		//   parties are named by the interestEntries not matched (by name) to a confirmation's
		//   mailedTo; any remainder that can't be named is emitted with a blank party so the count
		//   still reconciles.
		const getMissingPieces = (policy: BatchPolicySummary): MissingPiece[] => {
			if (!selectedBatch.value || !selectedBatch.value.batchFiles) return [];

			const missing: MissingPiece[] = [];
			for (const bf of selectedBatch.value.batchFiles) {
				if (!bf.policies) continue;
				for (const p of bf.policies) {
					if (p.policyNumber !== policy.policyNumber || p.transactionType !== policy.transactionType) continue;

					const confirmations = p.printConfirmation ?? [];

					if (isInterestGroup(bf.batchName)) {
						const parties = p.interestEntries ?? [];
						const shortfall = Math.max(0, parties.length - confirmations.length);
						if (shortfall === 0) continue;

						const confirmedNames = confirmations
							.map(c => (c.mailedTo ?? '').trim().toUpperCase())
							.filter(n => n.length > 0);
						const unmatched = parties.filter(e => {
							const name = (e.interestName ?? '').trim().toUpperCase();
							return name.length === 0 || !confirmedNames.some(cn => cn === name || cn.startsWith(name) || name.startsWith(cn));
						});

						const named = unmatched.slice(0, shortfall);
						named.forEach(e => missing.push({ batchName: bf.batchName, party: e.interestName }));
						for (let i = named.length; i < shortfall; i++) missing.push({ batchName: bf.batchName, party: '' });
					} else if (confirmations.length === 0) {
						// Recipient comes from the batch-file entry (populated there, unlike the
						// top-level policy summary): the AGENT copy goes to the agency, every other
						// non-interest copy goes to the policyholder.
						const recipient = bf.batchName === 'AGENT'
							? (p.agencyName ?? '')
							: (p.policyHolderName ?? '');
						missing.push({ batchName: bf.batchName, party: recipient });
					}
				}
			}
			return missing;
		};

		// "Mailed" tab count — Exela-confirmed mail PIECES (not policies), matching the Print
		// Validation card. A policy in multiple batches / with interest parties contributes one
		// per confirmed piece. (The tab still filters the table by policy; only the count is
		// piece-based.) Reads 0 while validation is pending — nothing is confirmed mailed yet.
		const mailedPieceCount = computed(() => isValidationPending.value ? 0 : affidavitTotals.value.exela);

		// "Not Mailed" tab count — pieces MoE produced that Exela did not confirm (MoE − Exela),
		// i.e. the piece-level shortfall. Clamped at 0 if the vendor ever over-reports. Reads 0
		// while validation is pending — a piece isn't "not mailed" until we've actually checked.
		const notMailedPieceCount = computed(() =>
			isValidationPending.value ? 0 : Math.max(0, affidavitTotals.value.moe - affidavitTotals.value.exela));

		const selectPrintConfirmations = (policy: BatchPolicySummary & { printConfirmations?: PrintConfirmationDetail[] }) => {
			printConfirmationPolicy.value = policy.policyNumber;
			printConfirmationDetails.value = policy.printConfirmations ?? getPrintConfirmations(policy);
			printMissingPieces.value = getMissingPieces(policy);
			overlayPrintConfirmations.value = true;
		};

		const successRate = computed(() => {
			if (!selectedBatch.value || selectedBatch.value.totalPoliciesProcessed === 0) return 0;
			return ((successCount.value / selectedBatch.value.totalPoliciesProcessed) * 100).toFixed(1);
		});

		const excludedRate = computed(() => {
			if (!selectedBatch.value || selectedBatch.value.totalPoliciesProcessed === 0) return 0;
			return ((excludedCount.value / selectedBatch.value.totalPoliciesProcessed) * 100).toFixed(1);
		});

		const failureRate = computed(() => {
			if (!selectedBatch.value || selectedBatch.value.totalPoliciesProcessed === 0) return 0;
			return ((failedCount.value / selectedBatch.value.totalPoliciesProcessed) * 100).toFixed(1);
		});

		const filteredPolicies = computed(() => {
			if (!selectedBatch.value || !selectedBatch.value.policySummaries) return [];

			return selectedBatch.value.policySummaries.filter(policy => {
				if (statusFilter.value === 'success') return policy.successful;
				if (statusFilter.value === 'excluded') return !policy.docProdError && (policy.excludedByTracker || policy.excludedByDocProd);
				if (statusFilter.value === 'failed') return policy.docProdError || (!policy.successful && !policy.excludedByTracker && !policy.excludedByDocProd);
				if (statusFilter.value === 'paperless') return policy.isPaperless;
				if (statusFilter.value === 'notpaperless') return !policy.isPaperless;
				// Mailed / Not Mailed are post-validation determinations: while pending, no printable
				// policy is classified either way (it shows "Pending" in the table instead).
				if (statusFilter.value === 'confirmed') return !isValidationPending.value && isFullyMailed(policy);
				if (statusFilter.value === 'notconfirmed') return !isValidationPending.value && hasPrintBatch(policy) && !isFullyMailed(policy);
				return true;
			});
		});

		const tableHeaders = [
			{ title: 'Policy Number', key: 'policyNumber', sortable: true, align: 'center' as const },
			{ title: 'Transaction Type', key: 'transactionType', sortable: true, align: 'center' as const },
			{ title: 'Amendment', key: 'amendmentNumber', sortable: true, align: 'center' as const },
			{ title: 'Status', key: 'statusLabel', sortable: true, align: 'center' as const },
			{ title: 'Pages', key: 'totalPages', sortable: true, align: 'center' as const },
			{ title: 'Processing Time', key: 'processingTime', sortable: false, align: 'center' as const },
			{ title: 'Form Codes (Primary)', key: 'formCodes', sortable: false, align: 'center' as const },
			{ title: 'Batch Name', key: 'batchName', sortable: true, align: 'center' as const },
			{ title: 'Is Paperless', key: 'isPaperlessLabel', sortable: true, align: 'center' as const },
			{ title: 'Print Status', key: 'printStatus', sortable: true, align: 'center' as const },
			{ title: 'Details', key: 'details', sortable: false, align: 'center' as const }
		]; const getProcessingTime = (policy: BatchPolicySummary): string => {
			if (!policy.startTime || !policy.endTime) return 'N/A';
			const start = new Date(policy.startTime);
			const end = new Date(policy.endTime);
			const seconds = (end.getTime() - start.getTime()) / 1000;
			return `${seconds.toFixed(1)}s`;
		};

		const getFailureReason = (policy: BatchPolicySummary): string => {
			if (policy.excludedByDocProd && !policy.docProdWipIncomplete && !policy.docProdError && !policy.successful) return 'BC Print';
			else if (policy.excludedByTracker) {
				if (policy.trackerIsCompleted) {
					return 'Tracker has already been completed';
				}
				if (policy.trackerDocumentRulesNotExecuted) {
					return 'Tracker Document Rules have not been executed (Never reached Ghost Draft)';
				}
				if (policy.trackerHasIncompletePolicyForms) {
					return 'Tracker has incomplete policy forms';
				}
				if (policy.trackerHasIncompleteInteractiveForms) {
					return 'Tracker has incomplete interactive forms';
				}
				return 'Excluded by Tracker';
			}
			else if (policy.docProdError) return 'Excluded by DocProd (DocProd Error)';
			else if (policy.excludedByDocProd) return 'Excluded by DocProd (WIP Incomplete)';
			else if (policy.errorMessages && policy.errorMessages.length > 0) {
				return policy.errorMessages.join('; ');
			}
			return 'Unknown';
		};

		const getArchivalStatus = (policy: BatchPolicySummary): string => {
			if (policy.cloudArchived && policy.locallyArchived) return 'Saved to OnBase';
			if (policy.cloudArchived) return 'Archived to cloud only';
			if (policy.locallyArchived) return 'Archived locally only';
			return 'Not archived';
		};

		const getBatchName = (policyNumber: string): string => {
			if (!selectedBatch.value || !selectedBatch.value.batchFiles) return 'N/A';

			const names: string[] = [];
			for (const batchFile of selectedBatch.value.batchFiles) {
				const foundPolicy = batchFile.policies.find(p => p.policyNumber === policyNumber);
				if (foundPolicy) {
					names.push(batchFile.batchName);
				}
			}
			return names.length > 0 ? names.join(', ') : 'N/A';
		};

		// True when the policy belongs to at least one print batch. When false, there is no
		// print batch for the document (e.g. paperless) so it is never mailed — its print
		// status is reported as N/A rather than "Not Mailed".
		const hasPrintBatch = (policy: BatchPolicySummary): boolean =>
			getBatchName(policy.policyNumber) !== 'N/A';

		// Rows for the table: copies of the status-filtered policies with the
		// computed display columns (Processing Time, Batch Name, Details) added as
		// real fields so the v-data-table `search` prop can match against them.
		// Originals in selectedBatch are never mutated, so export and counts are unaffected.
		const toDisplayRow = (policy: typeof filteredPolicies.value[0]) => {
			const printConfirmations = getPrintConfirmations(policy);
			const batchName = getBatchName(policy.policyNumber);
			const expectedPieces = getExpectedPieces(policy);
			// Pieces expected but not confirmed by the vendor — shown on Partial/Not Mailed chips.
			const missingPieces = Math.max(0, expectedPieces - printConfirmations.length);
			// No print batch → N/A (known regardless of validation). Otherwise, until validation
			// runs → Pending (we don't know yet). Once validated: all expected pieces confirmed →
			// Mailed; some but not all → Partial; none → Not Mailed.
			const printStatus = batchName === 'N/A'
				? 'N/A'
				: isValidationPending.value ? 'Pending'
					: (expectedPieces > 0 && printConfirmations.length >= expectedPieces) ? 'Mailed'
						: printConfirmations.length > 0 ? 'Partial'
							: 'Not Mailed';
			return {
				...policy,
				processingTime: getProcessingTime(policy),
				batchName,
				details: policy.successful ? getArchivalStatus(policy) : getFailureReason(policy),
				isPaperlessLabel: policy.isPaperless ? 'Yes' : 'No',
				printConfirmations,
				printStatus,
				missingPieces,
				expectedPieces,
				statusLabel: policy.docProdError
					? 'Failed'
					: policy.successful
						? 'Success'
						: (policy.excludedByTracker || policy.excludedByDocProd)
							? 'Excluded'
							: 'Failed'
			};
		};

		const displayRows = computed(() => filteredPolicies.value.map(toDisplayRow));

		const downloadBatchSummary = () => {
			if (!selectedBatch.value) return;

			try {
				const jsonData = JSON.stringify(selectedBatch.value, null, 2);
				const blob = new Blob([jsonData], { type: 'application/json' });
				const url = URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = url;
				link.download = `batch-summary-${formatBatchDateTime(selectedBatch.value.dateStarted).replaceAll(/[:\s]/g, '-')}.json`;
				document.body.appendChild(link);
				link.click();
				link.remove();
				URL.revokeObjectURL(url);
			} catch (error) {
				const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
				tell.error(`Failed to download batch summary: ${errorMessage}`);
				console.error('Error downloading batch summary:', error);
			}
		};

		const clearDateFilter = async () => {
			fromDate.value = '';
			toDate.value = '';
			hasSearched.value = false;
			await fetchBatchSummaries();
		};

		const fetchBatchSummaries = async () => {
			isWaiting.value = true;
			try {
				const request = new GetBatchSummariesRequest({
					limit: hasDateFilter.value ? undefined : 10,
					fromDate: fromDate.value ? new Date(fromDate.value) : undefined,
					toDate: toDate.value ? new Date(toDate.value) : undefined
				});

				const result = await getBatchSummaries(request);

				// Clear selection before updating summaries
				selectedBatchId.value = '';
				batchSummaries.value = result;
				hasSearched.value = true;

				// Set the first batch as selected by default after Vue updates
				if (result.length > 0) {
					selectedBatchId.value = result[0].id;
				}
			} catch (error) {
				console.error('Error fetching batch summaries:', error);
			} finally {
				isWaiting.value = false;
			}
		};

		onMounted(async () => {
			await fetchBatchSummaries();
		});

		return {
			batchSummaries,
			selectedBatchId,
			statusFilter,
			tableSearch,
			batchOptions,
			selectedBatch,
			successCount,
			excludedCount,
			failedCount,
			paperlessCount,
			notPaperlessCount,
			totalPages,
			validationStatus,
			validationStatusLabel,
			validationStatusColor,
			validationStatusBorderClass,
			batchValidationDisplay,
			mailedPieceCount,
			notMailedPieceCount,
			successRate,
			excludedRate,
			failureRate,
			displayRows,
			tableHeaders,
			overlayViewForms,
			batchPolicySummary,
			overlayPrintConfirmations,
			printConfirmationDetails,
			printMissingPieces,
			printConfirmationPolicy,
			overlayAffidavit,
			affidavitHeaders,
			affidavitRows,
			affidavitTotals,
			affidavitRowProps,
			isWaiting,
			fromDate,
			toDate,
			hasDateFilter,
			hasSearched,
			selectBatchPolicySummary,
			selectPrintConfirmations,
			getProcessingTime,
			getFailureReason,
			getArchivalStatus,
			getBatchName,
			downloadBatchSummary,
			fetchBatchSummaries,
			clearDateFilter
		};
	}
}
</script>

<style scoped lang="scss">
.card-border-left {
	border-left: 4px solid;
}

.card-border-blue {
	border-left-color: rgb(33, 150, 243); // Blue
}

.card-border-green {
	border-left-color: rgb(76, 175, 80); // Green
}

.card-border-orange {
	border-left-color: rgb(255, 152, 0); // Orange
}

.card-border-red {
	border-left-color: rgb(244, 67, 54); // Red
}

.card-border-purple {
	border-left-color: rgb(156, 39, 176); // Purple
}

.card-border-grey {
	border-left-color: rgb(158, 158, 158); // Grey — pending / not yet validated
}

.text-purple {
	color: rgb(156, 39, 176);
}

.text-blue {
	color: rgb(33, 150, 243);
}

.text-orange {
	color: rgb(255, 152, 0);
}

// Affidavit of Mailing row tints. Applied via row-props on the v-data-table,
// which renders its rows in a child component, so :deep is required to reach them.
:deep(.aom-row-match) td {
	background-color: rgba(76, 175, 80, 0.18) !important; // Green
}

:deep(.aom-row-mismatch) td {
	background-color: rgba(244, 67, 54, 0.18) !important; // Red
}
</style>
